/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import {ComponentType} from 'react';
import BatteryLowStatusIcon from '@ion/dls/src/icons/current/BatteryLowStatusIcon';
import HarshEventIcon from '@ion/dls/src/icons/current/HarshEventIcon';
import IgnitionOffIcon from '@ion/dls/src/icons/current/IgnitionOffIcon';
import IgnitionOnIcon from '@ion/dls/src/icons/current/IgnitionOnIcon';
import InputEventIcon from '@ion/dls/src/icons/current/InputEventIcon';
import StopEventIcon from '@ion/dls/src/icons/current/StopEventIcon';
import {dlsZIndex} from '@ion/dls/src/theme/theme';
import {DateSelectorValues, IconProps, TimeModes} from '@ion/dls/src/types/types';

// one row of the legend - the artwork to draw it with, and the locale key
// naming the event it stands for ...
export type TimelineLegendEntry = {
  label: string;
  Icon: ComponentType<IconProps>;
};

// the range the design allows a timeline to span, in hours ...
export const MIN_DURATION_HOURS = 1;
export const MAX_DURATION_HOURS = 24;

// the durations offered as one-click presets, and the duration a timeline
// opens on when the caller names none ...
export const PRESET_DURATIONS = [2, 6, 12, 24];
export const DEFAULT_DURATION = 2;

// every duration the custom selector offers - the full 1..24 hour range, of
// which exactly one may be chosen ...
export const CUSTOM_DURATIONS: number[] = new Array(MAX_DURATION_HOURS - MIN_DURATION_HOURS + 1)
  .fill(MIN_DURATION_HOURS)
  .map((first: number, index: number) => first + index);

// the legend is fixed copy against fixed artwork, so it is listed once here
// rather than rebuilt per render. Each entry names the icon rather than
// holding a drawn one, which leaves the drawing - and the size it is drawn
// at - to the component, and this module free of JSX. `label` is a locale
// key, resolved at render so the copy follows the active language ...
export const LEGEND_ENTRIES: TimelineLegendEntry[] = [
  {label: 'ignitionOn', Icon: IgnitionOnIcon},
  {label: 'stop', Icon: StopEventIcon},
  {label: 'ignitionOff', Icon: IgnitionOffIcon},
  {label: 'batteryLow', Icon: BatteryLowStatusIcon},
  {label: 'harshEvent', Icon: HarshEventIcon},
  {label: 'inputEvent', Icon: InputEventIcon}
];

// the panel the design gives the modal - 360px wide with a 12px radius, on
// the `shadow1` token, which is that spec's drop shadow exactly ...
export const PANEL_WIDTH = 360;
export const PANEL_RADIUS = 12;
export const SECTION_PADDING = 16;

// the height the design gives a full width row of the panel, the view by
// row among them, and the taller one it gives the footer ...
export const SECTION_ROW_HEIGHT = 44;
export const FOOTER_HEIGHT = 64;

// the design boxes the custom selector at 265x44, against the 200x48 the
// DLS select stands at by default ...
export const CUSTOM_SELECT_WIDTH = 265;
export const CUSTOM_SELECT_HEIGHT = 44;
export const CUSTOM_SELECT_RADIUS = 8;

// the close control's own box, shared by the Timeline and History panels and
// the limit error so the X reads the same wherever it is - a 24px white box
// on a 0.5px border, the 16px icon centred in it with no padding ...
export const CLOSE_BUTTON_SIZE = 24;
export const CLOSE_BUTTON_BORDER_WIDTH = 0.5;

// the separator, the legend box and the close control carry no text of their
// own, so they are addressed by test id ...
export const TIMELINE_TEST_ID = 'timeline-modal';
export const TIMELINE_CLOSE_TEST_ID = 'timeline-close';
export const TIMELINE_LEGEND_TEST_ID = 'timeline-legend';
export const TIMELINE_CUSTOM_TEST_ID = 'timeline-custom-durations';

// the modes the control offers, in the order the design lays them out ...
export const TIME_MODES = [TimeModes.live, TimeModes.timeline, TimeModes.history];

// shared so the default never hands the control a fresh array per render ...
export const EMPTY_DISABLED_MODES: TimeModes[] = [];

// as above - the timeline panel takes no configuration of its own by
// default, and a fresh object per render would defeat that control's `memo`.
// Left unannotated rather than typed as `TimelinePanelProps`: that type
// lives in a `.tsx`, and importing one into a `.ts` breaks Fast Refresh -
// the empty object is checked against it at the call site regardless ...
export const EMPTY_TIMELINE_PROPS = {};

// the gap the design leaves between the control and the panel it opens ...
export const TIMELINE_PANEL_OFFSET = 8;

// the map component the mode's panel is mounted as. The toolbar itself sits
// inside the map view's own UI layer, which holds its own stacking context -
// so the panel is carried out to a portal, on the theme's
// `mapTimeModeContainer` layer: the one above the tracking filter bar's, so
// the scrim dims its chips too, and below the tracking search's scrim. That
// also puts it above the main menu, which the scrim is kept clear of by its
// size - see `TIME_MODE_SCRIM_BOUNDS_ID` ...
export const TIME_MODE_PANEL_CONTAINER_ID = 'map-timeMode-container';

/**
 * Determines the layer a time mode panel's portal sits on - the theme's
 * `mapTimeModeContainer` layer, which the theme holds as a string and is
 * read here as a number. Read from the active theme when called rather than
 * once at load, so call it at render or where the styles are built.
 *
 * ```typescript
 *
 *   import {getTimeModePanelZIndex} from
 *     '@ion/dls/src/maps/components/timeMode/current/constants';
 *   ...
 *
 *   <IonPortal id={TIME_MODE_PANEL_CONTAINER_ID}
 *     zIndex={getTimeModePanelZIndex()} />
 *
 * ```
 *
 * @returns The panel's z-index.
 */
export const getTimeModePanelZIndex = (): number => Number(dlsZIndex('mapTimeModeContainer'));

/**
 * Determines the layer a time mode panel's own menu is raised to - one over
 * the panel's portal, since the menu is portalled to the body on its own and
 * would otherwise be left on the theme's popover layer beneath the panel.
 * Read from the active theme when called, as the panel's own layer is.
 *
 * ```typescript
 *
 *   import {getTimeModeMenuZIndex} from
 *     '@ion/dls/src/maps/components/timeMode/current/constants';
 *   ...
 *
 *   const useStyles = makeStyles(() => ({
 *     menu : {zIndex: getTimeModeMenuZIndex()}
 *   }));
 *
 * ```
 *
 * @returns The menu's z-index, one over the panel's.
 */
export const getTimeModeMenuZIndex = (): number => getTimeModePanelZIndex() + 1;

// the scrim and the panel share the portal's layer, so they are ordered
// against each other rather than against the page - the portal itself is
// what carries them over the map, the grid and the filter bar ...
export const TIME_MODE_SCRIM_LAYER = 0;
export const TIME_MODE_PANEL_LAYER = 1;

// the page content the scrim is bounded to - the application layout's own
// root, which sits beside the main menu rather than under it. Covering that
// box alone is what keeps the menu above the scrim; where it is not on the
// page the scrim covers the whole window ...
export const TIME_MODE_SCRIM_BOUNDS_ID = 'layout-root';

// the scrim is a plain sheet of colour, so it is addressed by test id ...
export const TIMELINE_SCRIM_TEST_ID = 'timeline-scrim';

// the input legend is drawn only for the input view, so it is addressed
// apart from the asset legend beside it ...
export const TIMELINE_INPUT_LEGEND_TEST_ID = 'timeline-input-legend';
export const TIMELINE_INPUT_CHIPS_TEST_ID = 'timeline-input-chips';

// the box the colour bar is drawn into. It is an SVG built at this exact
// size rather than scaled, so the width is what leaves the bar clear of the
// label beside it inside the panel's 16px gutters ...
export const COLOR_BAR_WIDTH = 200;
export const COLOR_BAR_HEIGHT = 20;

// the bar draws its own tick labels, and carries the face for them as a CSS
// string rather than a class - it is serialised into the SVG ...
export const COLOR_BAR_FONT_STYLE = `
  font-family: Inter, Arial, Helvetica, sans-serif;
  font-weight: 500;
  font-style: normal;
  font-size: 8px;
  text-transform: uppercase;
`;

// the control the panel is placed against. The panel is mounted in a portal
// of its own, so it cannot be positioned against the control by nesting -
// it finds the control by this id and measures it instead ...
export const TIME_MODE_CONTROL_ID = 'map-timeMode-control';

// the separator carries no text of its own, so it is addressed by test id ...
export const DIVIDER_TEST_ID = 'timemode-divider';

// the box the design gives the tooltip. Its 8px radius is already what the
// DLS tooltip applies, so only the width and height are stated here ...
export const TOOLTIP_WIDTH = 246;
export const TOOLTIP_HEIGHT = 91;

// the tooltip's own 4px/8px padding, which sits outside the content the
// control renders and, under the default `content-box`, adds to whatever
// width is set - so the box is sized by sizing the content within it, and
// the tooltip's own `width` prop is deliberately left alone. See the
// `padding` on the default variant in Tooltip.tsx ...
export const TOOLTIP_VERTICAL_PADDING = 8;
export const TOOLTIP_HORIZONTAL_PADDING = 16;
export const TOOLTIP_RADIUS = 8;

// the preset the History panel opens on when the caller names none ...
export const DEFAULT_HISTORY_PRESET = DateSelectorValues.Today;

// the History modal and its interactive pieces, addressed by test id ...
export const HISTORY_TEST_ID = 'history-modal';
export const HISTORY_CLOSE_TEST_ID = 'history-close';
export const HISTORY_CUSTOM_TEST_ID = 'history-custom-range';
export const HISTORY_CALENDAR_TEST_ID = 'history-calendar';
export const HISTORY_TIME_PICKER_TEST_ID = 'history-time-picker';

// the card the design gives the limit error - 318x240, on the mode panels'
// 12px radius. The height is a minimum rather than a fixed one, so a
// translation that runs to an extra line grows the card instead of being
// clipped by it ...
export const LIMIT_ERROR_WIDTH = 318;
export const LIMIT_ERROR_MIN_HEIGHT = 240;

// the header row the design gives the title and its close control - full
// card width, 56px tall, inset 16px on the left and 8px on the right ...
export const LIMIT_ERROR_HEADER_HEIGHT = 56;
export const LIMIT_ERROR_HEADER_PADDING_LEFT = 16;
export const LIMIT_ERROR_HEADER_PADDING_RIGHT = 8;

// the side inset of everything below the header - the copy and the actions
// sit on the header's 16px ...
export const LIMIT_ERROR_CONTENT_PADDING = 16;

// the message row's own top and bottom inset, and the gap between whatever
// it stacks - two lines of copy at 20px make up the design's 56px row ...
export const LIMIT_ERROR_MESSAGE_PADDING = 8;
export const LIMIT_ERROR_MESSAGE_GAP = 8;

// the usage row - full card width on an 8px inset all round, which leaves
// the tinted box it holds 8px wider than the copy to either side, and the
// gap between whatever that row stacks ...
export const LIMIT_ERROR_USAGE_PADDING = 8;
export const LIMIT_ERROR_USAGE_GAP = 8;

// the footer row the design gives the actions - full card width, 64px tall
// on an 8px inset all round, with 8px between the two actions ...
export const LIMIT_ERROR_FOOTER_HEIGHT = 64;
export const LIMIT_ERROR_FOOTER_PADDING = 8;
export const LIMIT_ERROR_FOOTER_GAP = 8;

// the DLS buttons wrap themselves in 3px of padding of their own, which a
// box the design sizes around a button has to leave room for ...
export const DLS_BUTTON_WRAPPER_PADDING = 3;

// the secondary action's box - 30px tall on an 8px inset, the button's own
// wrapper included, so the button itself is what that wrapper leaves. Its
// width is left to hug its label, the design's 135px being what "Stay in Live
// Mode" comes to ...
export const LIMIT_ERROR_ACTION_HEIGHT = 30 - (DLS_BUTTON_WRAPPER_PADDING * 2);
export const LIMIT_ERROR_ACTION_PADDING = 8 - DLS_BUTTON_WRAPPER_PADDING;

// the primary action's box - the design's 8px inset around the button, 48px
// tall to the footer's inset, less the wrapper the button already carries.
// The button itself is given the design's 112px as a minimum, which is what
// seats the secondary action where the design has it; a longer translation
// still grows it ...
export const LIMIT_ERROR_PRIMARY_ACTION_PADDING = 8 - DLS_BUTTON_WRAPPER_PADDING;

// the no assets card holds no minimum height - with neither figures nor a
// second action, the footer would otherwise be pushed far below the copy.
// Its Refine Filter sits as far under the message as the message sits under
// the title: the 56px header leaves 18px under the title's 20px line, and the
// message opens on its own 8px inset (26px). Under the message, its 8px inset,
// the footer's 8px and the button's 5px and own 3px already come to 24px, so
// the footer is let down by what is left ...
export const LIMIT_ERROR_TITLE_LINE_HEIGHT = 20;
export const LIMIT_ERROR_NO_ASSETS_FOOTER_OFFSET = (
  ((LIMIT_ERROR_HEADER_HEIGHT - LIMIT_ERROR_TITLE_LINE_HEIGHT) / 2 + LIMIT_ERROR_MESSAGE_PADDING) -
  (LIMIT_ERROR_MESSAGE_PADDING + LIMIT_ERROR_FOOTER_PADDING + LIMIT_ERROR_PRIMARY_ACTION_PADDING +
    DLS_BUTTON_WRAPPER_PADDING)
);
export const LIMIT_ERROR_PRIMARY_ACTION_MIN_WIDTH = 112;

// how long the card takes to animate away once its secondary action is
// chosen - the design's 140ms ease-out ...
export const LIMIT_ERROR_EXIT_DURATION = 140;
export const LIMIT_ERROR_EXIT_EASING = 'ease-out';

// the box the limit error seats its close control in, centred - 44px
// square, the touch target size ...
export const LIMIT_ERROR_CLOSE_CONTAINER_SIZE = 44;

// the usage box - an 8px inset and radius, and the gap between the warning
// icon and the figures beside it ...
export const LIMIT_ERROR_USAGE_BOX_PADDING = 8;
export const LIMIT_ERROR_USAGE_BOX_RADIUS = 8;
export const LIMIT_ERROR_USAGE_BOX_GAP = 4;

// the limit error and its close control, addressed by test id ...
export const LIMIT_ERROR_TEST_ID = 'timemode-limit-error';
export const LIMIT_ERROR_CLOSE_TEST_ID = 'timemode-limit-error-close';
export const LIMIT_ERROR_USAGE_TEST_ID = 'timemode-limit-error-usage';

// the limit warning a drawer's footer carries over its actions - the limit
// error's own warm tint and figures, set in a strip the footer's width (592px
// in the 624px drawer), 56px tall: two 16px lines 8px apart on an 8px inset,
// with an 8px radius and 4px between the icon and its copy. The height is a
// minimum, so a translation that runs to an extra line grows the strip rather
// than being clipped by it ...
export const LIMIT_WARNING_TEST_ID = 'timemode-limit-warning';
export const LIMIT_WARNING_MIN_HEIGHT = 56;
export const LIMIT_WARNING_PADDING = 8;
export const LIMIT_WARNING_RADIUS = 8;
export const LIMIT_WARNING_GAP = 8;
export const LIMIT_WARNING_ICON_GAP = 4;
export const LIMIT_WARNING_ICON_SIZE = 16;

// the ceiling a time mode may report over, as assets × days, and the days a
// timeline spans for it - its 24 hour maximum reads as one whole day ...
export const TIME_MODE_ASSET_DAY_LIMIT = 120;
export const TIMELINE_LIMIT_DAYS = 1;

// the fewest days a History range can span - a single day. Where the assets
// on the map exceed the limit over even that, no range could be run, so
// History is refused before its panel opens ...
export const HISTORY_MIN_DAYS = 1;
